import { useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import ProductCard from "../components/ProductCard/ProductCard";
import usePage from "../hooks/usePage";
import { transformProductsData } from "../utils/dataTransformer";
import "./ProductPage.css";

const PRODUCT_SLUG = 'home';

const ProductPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { page, loading, error } = usePage(PRODUCT_SLUG);

  const sections = useMemo(() => {
    const allSections =
      page?.acf?.product_groups
        ? transformProductsData(page.acf.product_groups)
        : [];
    return allSections.filter(section => section.label !== 'SERVICE MODULE');
  }, [page]);

  const categories = useMemo(() => sections.map((s) => s.label), [sections]);
  const isReady = !loading && categories.length > 0;

  const activeCategory = useMemo(() => {
    if (sections.length === 0) return null;
    const categoryFromUrl = searchParams.get("category");
    if (categoryFromUrl) {
      const normalized = decodeURIComponent(categoryFromUrl).toUpperCase();
      return categories.includes(normalized) ? normalized : categories[0];
    }
    return categories[0];
  }, [searchParams, sections, categories]);

  const currentSection = sections.find((s) => s.label === activeCategory);
  const currentProducts = currentSection?.slides || [];

  const handleTabClick = (cat) => {
    navigate(`?category=${encodeURIComponent(cat)}`);
  };

  if (loading || !isReady) {
    return (
      <div className="app-loading">
        <div className="app-loading__spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <section>
        <div className="product-header">
          <div className="products-hero__content header-container">
            <h1 className="products-hero__title">Products</h1>
            <p className="products-hero__description">
              Mission-ready systems for Defence, Aerospace, and Industrial
              applications — engineered with precision and built to last.
            </p>
          </div>
        </div>b
        <main className="page-main">
          <div className="container">
            <div className="products-section__error">
              <p>{error || "No products available at the moment."}</p>
            </div>
          </div>
        </main>
      </section>
    );
  }


  return (
    <section>
      <div className="product-header">
        <div className="products-hero__content header-container">
          <h2 className="products-hero__title">Products</h2>
          <p className="products-hero__description">
            Mission-ready systems for Defence, Aerospace, and Industrial
            applications — engineered with precision and built to last.
          </p>
        </div>
      </div>

      <main className="page-main">
        <div className="container">
          <div className="products-tabs" role="tablist">
            {categories.map((cat) => (
              <button
                key={cat}
                role="tab"
                aria-selected={activeCategory === cat}
                className={`products-tab ${activeCategory === cat ? "products-tab--active" : ""
                  }`}
                onClick={() => handleTabClick(cat)}
              >
                {cat.charAt(0) + cat.slice(1).toLowerCase()}
              </button>
            ))}
          </div>

          <div className="products-grid" key={activeCategory}>
            {currentProducts.map((product) => (
              <ProductCard
                key={product.slug}
                title={product.title}
                description={product.description}
                image={product.imageUrl}
                slug={product.slug}
              />
            ))}
          </div>
        </div>
      </main>
    </section>
  );
};

export default ProductPage;